import AsyncStorage from '@react-native-async-storage/async-storage';
import axios, { AxiosError, AxiosRequestConfig, Method } from 'axios';
import { Platform } from 'react-native';
import { CommunityCode, getCommunityByCode } from '../constants/communities';
import { APP_VERSION } from '../constants/version';

export type ApiHostPrefix = 'www' | 'staging';

const API_HOST_PREFIX_STORAGE_KEY = 'apiHostPrefix';

// Cap how long a request can hang (axios defaults to no timeout, so a dead
// connection stalls for minutes on web). Multipart uploads get a longer cap
// since large photos on slow connections legitimately take a while.
const REQUEST_TIMEOUT_MS = 15000;
const UPLOAD_TIMEOUT_MS = 120000;

export class ApiService {
  // Static variables for authentication data
  private static _currentCommunity: CommunityCode | undefined;
  private static _userName: string | undefined;
  private static _authToken: string | undefined;
  // Initialized from Platform.OS so requests that run before configure()
  // (e.g. cleanup calls from a signed-out cold start) still send valid headers.
  private static _reactPlatform: string = Platform.OS;
  private static _apiHostPrefix: ApiHostPrefix = 'www';
  private static _hostPrefixListeners = new Set<() => void>();

  private static notifyHostPrefixChange(): void {
    this._hostPrefixListeners.forEach((listener) => listener());
  }

  static subscribeHostPrefix(listener: () => void): () => void {
    this._hostPrefixListeners.add(listener);
    return () => {
      this._hostPrefixListeners.delete(listener);
    };
  }

  // Configuration method
  static configure(
    community: CommunityCode | undefined,
    userName: string | undefined,
    authToken: string | undefined,
    reactPlatform: string
  ): void {
    this._currentCommunity = community;
    this._userName = userName;
    this._authToken = authToken;
    this._reactPlatform = reactPlatform;
  }

  static updateAuthToken(authToken: string | undefined): void {
    this._authToken = authToken;
  }

  static get hasCommunity(): boolean {
    return this._currentCommunity !== undefined;
  }

  private static getClientPlatformHeader(): string {
    return this._reactPlatform === 'web' ? 'WEBAPP' : this._reactPlatform.toUpperCase();
  }

  private static getConfig(data?: any, extraHeaders?: Record<string, string>): AxiosRequestConfig {
    const headers: Record<string, string> = {
      X_CLIENT_VERSION: APP_VERSION,
      X_CLIENT_PLATFORM: this.getClientPlatformHeader(),
    };

    // Add Authorization header for mobile platforms
    if (this._reactPlatform !== 'web' && this._authToken) {
      headers.Authorization = `Bearer ${this._authToken}`;
    }

    // Set content-type based on data type
    if (data !== undefined) {
      if (data instanceof FormData) {
        headers['Content-Type'] = 'multipart/form-data';
      } else {
        headers['Content-Type'] = 'application/json; charset=utf-8';
      }
    }

    // Merge any extra headers (e.g., per-request custom headers)
    if (extraHeaders) {
      Object.assign(headers, extraHeaders);
    }

    return {
      headers,
      timeout: data instanceof FormData ? UPLOAD_TIMEOUT_MS : REQUEST_TIMEOUT_MS,
      // For web, cookies will be sent automatically
      withCredentials: this._reactPlatform === 'web',
    };
  }

  static getApiHostPrefix(): ApiHostPrefix {
    return this._apiHostPrefix;
  }

  static setApiHostPrefix(prefix: ApiHostPrefix): void {
    if (this._apiHostPrefix === prefix) return;
    this._apiHostPrefix = prefix;
    this.notifyHostPrefixChange();
  }

  static getCommunityOrigin(community: CommunityCode): string {
    return `https://${this._apiHostPrefix}.${getCommunityByCode(community).domain}`;
  }

  static async loadStoredHostPrefix(): Promise<void> {
    const previousPrefix = this._apiHostPrefix;
    try {
      const stored = await AsyncStorage.getItem(API_HOST_PREFIX_STORAGE_KEY);
      this._apiHostPrefix = stored === 'staging' ? 'staging' : 'www';
    } catch (error) {
      this._apiHostPrefix = 'www';
      console.warn('Failed to load API host prefix, defaulting to www:', error);
    }
    if (this._apiHostPrefix !== previousPrefix) {
      this.notifyHostPrefixChange();
    }
  }

  static async persistHostPrefix(prefix: ApiHostPrefix): Promise<void> {
    const previousPrefix = this._apiHostPrefix;
    this._apiHostPrefix = prefix;
    try {
      if (prefix === 'www') {
        await AsyncStorage.removeItem(API_HOST_PREFIX_STORAGE_KEY);
      } else {
        await AsyncStorage.setItem(API_HOST_PREFIX_STORAGE_KEY, prefix);
      }
    } catch (error) {
      this._apiHostPrefix = 'www';
      console.warn('Failed to persist API host prefix, defaulting to www:', error);
      if (this._apiHostPrefix !== previousPrefix) {
        this.notifyHostPrefixChange();
      }
      throw error;
    }
    if (this._apiHostPrefix !== previousPrefix) {
      this.notifyHostPrefixChange();
    }
  }

  private static getApiBaseUrl(): string {
    if (this._currentCommunity === undefined) {
      throw new Error('No community selected');
    }

    return `${this.getCommunityOrigin(this._currentCommunity)}/api/v6`;
  }

  static async request<T>(
    method: Method,
    endpoint: string,
    data?: any,
    params?: object,
    extraHeaders?: Record<string, string>
  ): Promise<T> {
    try {
      const res = await axios({
        method,
        url: `${this.getApiBaseUrl()}/${endpoint}`,
        data,
        params,
        ...this.getConfig(data, extraHeaders),
      });

      return res.data as T;
    } catch (e) {
      if (e instanceof AxiosError) {
        const status = e.response?.status;
        const responseData = e.response?.data;
        const errorCode = responseData?.error as string | undefined;
        const baseMessage =
          responseData?.Message ||
          responseData?.message ||
          (status === 404
            ? 'The content you are looking for is not available'
            : e.code === AxiosError.ECONNABORTED || e.code === AxiosError.ETIMEDOUT
              ? 'The request timed out. Please check your connection and try again.'
              : e.message);
        const message = status ? `${baseMessage} (${status})` : baseMessage;
        
        throw new ApiError(message, status, responseData, e, errorCode);
      }
      throw e;
    }
  }

  /**
   * Sends a request to a specific community's API using explicit credentials,
   * regardless of which community this service is currently configured for.
   * Native only (Bearer auth); used for cleanup calls against a community the
   * user is not currently signed into (e.g. removing a push token).
   * @param method The HTTP method
   * @param community The community whose API to call
   * @param authToken That community's stored auth token (not the active session's)
   * @param endpoint The endpoint path relative to the API base
   * @param params Optional query parameters
   */
  static async requestForCommunity<T>(
    method: Method,
    community: CommunityCode,
    authToken: string,
    endpoint: string,
    params?: object
  ): Promise<T> {
    const res = await axios({
      method,
      url: `${this.getCommunityOrigin(community)}/api/v6/${endpoint}`,
      params,
      headers: {
        X_CLIENT_VERSION: APP_VERSION,
        X_CLIENT_PLATFORM: this.getClientPlatformHeader(),
        Authorization: `Bearer ${authToken}`,
      },
      timeout: REQUEST_TIMEOUT_MS,
    });

    return res.data as T;
  }

  // Wrapper methods for specific HTTP methods
  static async get<T>(
    endpoint: string,
    params?: object
  ): Promise<T> {
    return this.request<T>('GET', endpoint, undefined, params);
  }

  static async post<T>(
    endpoint: string,
    data: any
  ): Promise<T> {
    return this.request<T>('POST', endpoint, data, undefined);
  }

  static async put<T>(
    endpoint: string,
    data: any
  ): Promise<T> {
    return this.request<T>('PUT', endpoint, data, undefined);
  }

  static async delete<T>(
    endpoint: string,
    params?: object,
    extraHeaders?: Record<string, string>
  ): Promise<T> {
    return this.request<T>('DELETE', endpoint, undefined, params, extraHeaders);
  }

  static async patch<T>(
    endpoint: string,
    data: any
  ): Promise<T> {
    return this.request<T>('PATCH', endpoint, data, undefined);
  }

  static getUrl(endpoint: string): string {
    return `${this.getApiBaseUrl()}/${endpoint}`;
  }

  static getCurrentCommunity(): CommunityCode | undefined {
    return this._currentCommunity;
  }

  static getUserName(): string | undefined {
    return this._userName;
  }

  static getReactPlatform(): string {
    return this._reactPlatform;
  }
}



// Custom error class for API errors

export class ApiError extends Error {
  status?: number;
  data?: any;
  axiosError?: AxiosError;
  /** Structured error code from v6 API responses (e.g. "INVALID_CURSOR", "PREMIUM_REQUIRED") */
  errorCode?: string;

  constructor(message: string, status?: number, data?: any, axiosError?: AxiosError, errorCode?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
    this.axiosError = axiosError;
    this.errorCode = errorCode;
  }
}